import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowDownTrayIcon,
  ArchiveBoxIcon,
  CalculatorIcon,
  DocumentChartBarIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";

const currentQuarter = () => Math.floor(new Date().getMonth() / 3) + 1;
const currentYear = () => new Date().getFullYear();

export const GestoriaHub: React.FC = () => {
  const [year, setYear] = useState(currentYear());
  const [quarter, setQuarter] = useState(currentQuarter());
  const [summary, setSummary] = useState<Record<string, number> | null>(null);
  const [expenseSummary, setExpenseSummary] = useState<Record<string, number> | null>(null);
  const [loading, setLoading] = useState(false);
  const [exportMsg, setExportMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [fiscalRes, expRes] = await Promise.all([
      apiService.getFiscalQuarterlySummary(year, quarter),
      apiService.getProfessionalExpensesQuarterly(year, quarter),
    ]);
    setSummary((fiscalRes.data as Record<string, number>) || null);
    setExpenseSummary((expRes.data as { summary?: Record<string, number> })?.summary || null);
    setLoading(false);
  }, [year, quarter]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadUrl = (path: string) => {
    const token = localStorage.getItem("auth_token");
    const base = process.env.REACT_APP_API_URL || "/api";
    const sep = path.includes("?") ? "&" : "?";
    return `${base}${path}${token ? `${sep}token=${encodeURIComponent(token)}` : ""}`;
  };

  const handleExport = async (kind: "csv" | "zip") => {
    setExportMsg("");
    try {
      const path =
        kind === "csv"
          ? `/professional-expenses/export/${year}/${quarter}.csv`
          : `/professional-expenses/export/${year}/${quarter}.zip`;
      const res = await fetch(downloadUrl(path), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
        },
      });
      if (!res.ok) throw new Error("Exportación fallida");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "csv" ? `gastos-T${quarter}-${year}.csv` : `tickets-T${quarter}-${year}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setExportMsg(kind === "csv" ? "CSV descargado" : "ZIP de tickets descargado");
    } catch {
      setExportMsg("No se pudo exportar. Compruebe sesión y gastos del trimestre.");
    }
  };

  const cards = [
    {
      id: "trimestral",
      title: "Export trimestral",
      desc: "Resumen fiscal del trimestre para tu gestoría.",
      icon: DocumentChartBarIcon,
      action: () => load(),
      actionLabel: "Actualizar resumen",
      metric: summary
        ? `${Number(summary.ingresos_base || 0).toFixed(2)} € ingresos`
        : loading
          ? "Cargando…"
          : "Sin datos",
    },
    {
      id: "iva",
      title: "IVA estimado",
      desc: "Resultado IVA del trimestre (ingresos − soportado).",
      icon: ReceiptPercentIcon,
      metric: summary
        ? `${Number(summary.resultado_iva_estimado || 0).toFixed(2)} €`
        : "—",
    },
    {
      id: "irpf",
      title: "IRPF / retenciones",
      desc: "Retenciones y base deducible de gastos profesionales.",
      icon: CalculatorIcon,
      metric: summary
        ? `${Number(summary.retenciones_irpf || summary.irpf_trimestre || 0).toFixed(2)} €`
        : "—",
    },
    {
      id: "zip",
      title: "ZIP tickets",
      desc: "Archivos adjuntos de gastos del trimestre.",
      icon: ArchiveBoxIcon,
      action: () => handleExport("zip"),
      actionLabel: "Descargar ZIP",
      metric: expenseSummary
        ? `${Number(expenseSummary.count || expenseSummary.totalGastos || 0)} gastos`
        : "Sin gastos",
    },
  ];

  return (
    <div className="space-y-7 max-w-5xl">
      <div>
        <p className="text-xs font-medium text-[#8898aa] uppercase tracking-wider mb-1">Gestoría</p>
        <h1 className="text-xl font-semibold text-[#0A2540] dark:text-[#e6edf3]">Centro fiscal</h1>
        <p className="text-sm text-[#425466] dark:text-[#8b949e] mt-0.5">
          Exportaciones y resúmenes trimestrales listos para tu asesoría.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={quarter}
          onChange={(e) => setQuarter(Number(e.target.value))}
          className="rounded-lg border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] text-[#0A2540] dark:text-[#e6edf3] px-3 py-2 text-sm focus:outline-none focus:border-[#635BFF] transition-colors"
        >
          {[1, 2, 3, 4].map((q) => (
            <option key={q} value={q}>T{q}</option>
          ))}
        </select>
        <input
          type="number"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-lg border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] text-[#0A2540] dark:text-[#e6edf3] px-3 py-2 text-sm w-28 focus:outline-none focus:border-[#635BFF] transition-colors"
        />
        <button
          type="button"
          onClick={() => handleExport("csv")}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#635BFF] text-white text-sm font-semibold px-4 py-2 hover:bg-[#4F46E5] shadow-sm transition-colors"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          CSV gastos
        </button>
      </div>

      {exportMsg && (
        <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">{exportMsg}</p>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.id}
              className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] p-6 shadow-stripe hover:shadow-stripe-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-[#635BFF]/10 p-2.5">
                  <Icon className="h-5 w-5 text-[#635BFF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-[#0A2540] dark:text-[#e6edf3]">{card.title}</h3>
                  <p className="text-xs text-[#8898aa] mt-1">{card.desc}</p>
                  <p className="text-xl font-semibold text-[#0A2540] dark:text-[#e6edf3] mt-3 tabular-nums">
                    {card.metric}
                  </p>
                  {card.action && (
                    <button
                      type="button"
                      onClick={card.action}
                      className="mt-3 text-xs font-medium text-[#635BFF] hover:text-[#4F46E5] transition-colors"
                    >
                      {card.actionLabel} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && !summary && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#E6E9F0] dark:border-[#30363d] p-12 text-center">
          <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center mb-3">
            <DocumentChartBarIcon className="h-6 w-6 text-[#635BFF]" />
          </div>
          <p className="text-sm text-[#425466] dark:text-[#8b949e]">
            No hay datos fiscales para T{quarter} {year}. Emite facturas o registra gastos.
          </p>
        </div>
      )}
    </div>
  );
};
